/**
 * Sorts rendered Vault items without changing their canonical hierarchy.
 *
 * Keeping sorting pure gives list and grid layouts identical ordering while
 * source ownership and authorization remain untouched.
 */
import type { ItemResponse } from '../types'

export type VaultSortKey = 'name' | 'updatedAt' | 'sizeBytes'
export type VaultSortDirection = 'asc' | 'desc'

export function sortVaultItems(
  items: ItemResponse[],
  key: VaultSortKey,
  direction: VaultSortDirection,
): ItemResponse[] {
  const factor = direction === 'asc' ? 1 : -1
  return [...items].sort((left, right) => {
    const a = sortValue(left, key)
    const b = sortValue(right, key)
    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b) * factor
    }
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    }) * factor
  })
}

function sortValue(entry: ItemResponse, key: VaultSortKey): string | number {
  if (key === 'updatedAt') return Date.parse(entry.item.updatedAt)
  if (key === 'sizeBytes') return entry.item.sizeBytes ?? -1
  return entry.item.name
}
